<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { toast } from "@/common/utils/toast";
import store from "@/store";

import {
  evaluationList,
  evaluationCreate,
} from "@/common/api/rest/evaluation/evaluation";

import type { EvaluationListType } from "@/common/types/evaluation";

import { PanelTheme } from "@/common/themes";

import {
  baseConfirm,
  cardEvaluation,
  baseInput,
  baseLoading,
  baseSelect,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const listRef = ref();
const searchInput = ref("");
const statusType = ref("PROGRESS");

const evaluations = ref<EvaluationListType[]>([]);

const loading = ref(false);
const hasScroll = ref(false);
const showModalAdd = ref(false);

const accountId = computed(() => route.params.id as string);
const userType = computed(() => store.getters.type as string);

const statusOptions = computed(() => [
  {
    option: t("evaluation.labels.in_progress"),
    value: "PROGRESS",
  },
  {
    option: t("evaluation.labels.started"),
    value: "STARTED",
  },
  {
    option: t("evaluation.labels.pending"),
    value: "PENDING",
  },
  {
    option: t("evaluation.labels.concluded"),
    value: "CONCLUDED",
  },
  {
    option: t("evaluation.labels.canceled"),
    value: "CANCELED",
  },
]);

function checkPermissions() {
  if (
    !userType.value ||
    userType.value === "ADMIN" ||
    userType.value === "MANAGER"
  )
    return;

  toast({ message: t("error.unauthorized"), type: "error" });
  router.push(`/${accountId.value}/home`);
}

function checkScroll() {
  if (!listRef.value) return false;

  hasScroll.value = listRef.value?.scrollHeight > listRef.value?.clientHeight;
}

async function fetchData() {
  if (!accountId.value) return;

  loading.value = true;

  evaluationList(accountId.value, {
    full: true,
    status: statusType.value,
    search: searchInput.value,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        evaluations.value = data.list;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loading.value = false;
      checkScroll();
    });
}

function addItem() {
  showModalAdd.value = true;
}

function addItemCancel() {
  showModalAdd.value = false;
}

function addItemConfirm() {
  showModalAdd.value = false;
  store.commit("SET_LOADING", true);

  evaluationCreate(accountId.value, {
    member_id: store.getters.user.member_id,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        fetchData();
        addItemCancel();
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function selectEvaluation(evaluation: EvaluationListType) {
  router.push(`/${accountId.value}/cockpit/${evaluation.evaluation_id}`);
}

function cleanSearch() {
  if (!searchInput.value) {
    fetchData();
  }
}

function searchValue() {
  fetchData();
}

function changeStatus() {
  localStorage.setItem("cockpit-status", statusType.value);

  fetchData();
}

watchEffect(() => {
  checkPermissions();
});

onMounted(() => {
  const status = localStorage.getItem("cockpit-status");
  statusType.value = status || "PROGRESS";

  fetchData();
});
</script>

<template>
  <panel-theme>
    <section class="cockpit-page">
      <section class="header">
        <base-select
          v-model="statusType"
          :value="statusType"
          :options="statusOptions"
          :is-empty="false"
          @change="changeStatus()"
        />

        <div class="search">
          <base-input
            v-model="searchInput"
            background="#fff"
            color="#000"
            :placeholder="t('common.labels.search')"
            @update:model-value="cleanSearch()"
            @key:enter="searchValue()"
          >
            <template #sufix>
              <div class="icon">
                <svg-icon icon="search" size="1.75rem" @click="searchValue()" />
              </div>
            </template>
          </base-input>
        </div>

        <div class="actions">
          <button class="btn btn-md btn-primary" @click="addItem()">
            <svg-icon icon="add" size="1.1rem" />
            <span>
              {{ t("common.labels.evaluation")?.toUpperCase() }}
            </span>
          </button>
        </div>
      </section>

      <section
        ref="listRef"
        class="evaluations"
        :class="{ scrollable: hasScroll }"
      >
        <div class="list">
          <template
            v-for="evaluation in evaluations"
            :key="evaluation.evaluation_id"
          >
            <card-evaluation
              :evaluation="evaluation"
              @click="selectEvaluation(evaluation)"
            />
          </template>
        </div>

        <template v-if="loading">
          <div class="flex items-center justify-center">
            <base-loading />
          </div>
        </template>

        <template v-if="!loading && evaluations.length === 0">
          <div class="empty">
            {{ t("evaluation.texts.empty_evaluation") }}
          </div>
        </template>
      </section>
    </section>

    <base-confirm
      :show="showModalAdd"
      :title="t('evaluation.modal.titles.add_evaluation').toUpperCase()"
      :message="t('evaluation.modal.messages.add_evaluation')"
      @cancel="addItemCancel()"
      @confirm="addItemConfirm()"
    />
  </panel-theme>
</template>

<style lang="scss" scoped>
.cockpit-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .header {
    display: grid;
    grid-template-columns: 18rem 2fr auto;
    justify-content: space-between;
    gap: 2rem;
    height: 2.9rem;

    .search {
      width: 100%;

      .icon {
        cursor: pointer;
        color: var(--text-off);

        &:hover {
          color: var(--primary);
        }
      }
    }

    .actions {
      display: flex;
      justify-content: flex-end;
      align-items: flex-start;
    }
  }

  .evaluations {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    height: calc(100dvh - 2.9rem - 1rem - 6rem);
    overflow: auto;

    &.scrollable {
      padding-inline-end: 0.5rem;
    }

    .list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding-block-end: 2rem;
    }

    .empty {
      padding-inline: 0.5rem;
    }
  }
}

@media screen and (max-width: 1024px) {
  .cockpit-page {
    .header {
      grid-template-columns: 2fr auto;
      gap: 2.5rem;

      .actions button {
        padding-inline: 0.75rem;

        span {
          display: none;
        }
      }
    }
    .evaluations {
      height: calc(100dvh - 2.9rem - 1rem - 6rem - 3.5rem);
    }
  }
}
</style>
